/**
 * Service de gestion des informations de la société OMAD SERVICES
 * 
 * Ce service fournit des utilitaires pour :
 * - Récupérer les informations de la société depuis Firestore
 * - Formater automatiquement les informations juridiques
 * - Calculer la TVA intracommunautaire
 * - Générer les en-têtes de documents
 */

import { doc, getDoc } from 'firebase/firestore';

/**
 * Récupère les informations de la société depuis Firestore
 * @param {Object} db - Instance Firestore
 * @returns {Promise<Object>} Informations de la société
 */
export const getCompanyInfo = async (db) => {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    return getDefaultCompanyInfo();
  } catch (error) {
    console.error('Erreur récupération informations société:', error);
    return getDefaultCompanyInfo();
  }
};

/**
 * Retourne les informations par défaut d'OMAD SERVICES
 * @returns {Object} Informations par défaut
 */
export const getDefaultCompanyInfo = () => {
  return {
    denominationSociale: 'OMAD SERVICES',
    sigle: 'OMAD',
    formeJuridique: 'SAS (Société par Actions Simplifiée)',
    siren: '922 592 662',
    siret: '922 592 662 00014',
    capitalSocial: '500 €',
    immatriculation: 'RCS Bourges, le 17 octobre 2022',
    codeNafApe: '8810A – Aide à domicile',
    siegeSocial: '30 bis Rue des Bondoires, 18230 Saint-Doulchard',
    president: 'Mme Virginie Cahagne',
    telephone: '06 67 72 73 30',
    email: 'contact@omadservices.com',
    emailSecondaire: 'contact@omadservices.com',
    siteWeb: 'www.omadservices.com',
    tvaIntracommunautaire: '',
    conditionsVente: '',
    mentionsLegales: '',
    organizationName: 'OMAD SERVICES',
    timeZone: 'Europe/Paris',
    country: 'France'
  };
};

/**
 * Calcule automatiquement le numéro de TVA intracommunautaire
 * @param {string} siren - Numéro SIREN
 * @param {string} countryCode - Code pays (FR par défaut)
 * @returns {string} Numéro de TVA intracommunautaire
 */
export const calculerTVAIntracommunautaire = (siren, countryCode = 'FR') => {
  if (!siren) return '';
  
  // Nettoyer le SIREN (supprimer les espaces)
  const sirenNettoye = siren.replace(/\s/g, '');
  
  // Vérifier que le SIREN a 9 chiffres
  if (!/^\d{9}$/.test(sirenNettoye)) {
    console.warn('SIREN invalide pour calcul TVA:', siren);
    return '';
  }
  
  // Calculer la clé TVA selon l'algorithme français
  const cleTVA = (12 + 3 * (parseInt(sirenNettoye) % 97)) % 97;
  const cleFormatee = cleTVA.toString().padStart(2, '0');
  
  return `${countryCode}${cleFormatee} ${sirenNettoye}`;
};

/**
 * Formate les informations juridiques pour l'affichage
 * @param {Object} companyInfo - Informations de la société
 * @returns {Object} Informations formatées
 */
export const formaterInformationsJuridiques = (companyInfo) => {
  const info = { ...getDefaultCompanyInfo(), ...companyInfo };
  
  // Calculer la TVA si non renseignée
  if (!info.tvaIntracommunautaire && info.siren) {
    info.tvaIntracommunautaire = calculerTVAIntracommunautaire(info.siren);
  }
  
  return {
    // Identité complète
    identiteComplete: `${info.denominationSociale} (${info.sigle})`,
    
    // Forme juridique complète
    formeJuridiqueComplete: `${info.formeJuridique} au capital de ${info.capitalSocial}`,
    
    // Adresse formatée
    adresseFormatee: info.siegeSocial,
    
    // Coordonnées complètes
    coordonnees: {
      telephone: info.telephone,
      email: info.email,
      emailSecondaire: info.emailSecondaire,
      siteWeb: info.siteWeb
    },
    
    // Informations légales
    informationsLegales: {
      siren: info.siren,
      siret: info.siret,
      tvaIntracommunautaire: info.tvaIntracommunautaire,
      immatriculation: info.immatriculation,
      codeNafApe: info.codeNafApe,
      president: info.president
    },
    
    // En-tête pour documents
    enteteDocument: `${info.denominationSociale} (${info.sigle})\n${info.formeJuridique}\nCapital : ${info.capitalSocial}\n${info.siegeSocial}\nSIREN : ${info.siren} - SIRET : ${info.siret}\n${info.tvaIntracommunautaire ? `TVA Intracommunautaire : ${info.tvaIntracommunautaire}` : ''}`,
    
    // Pied de page pour documents
    piedPageDocument: `${info.telephone ? `Tél : ${info.telephone}` : ''}${info.email ? ` - Email : ${info.email}` : ''}${info.siteWeb ? ` - Site : ${info.siteWeb}` : ''}`
  };
};

/**
 * Génère l'en-tête HTML pour les documents (devis, factures)
 * @param {Object} companyInfo - Informations de la société
 * @returns {string} HTML de l'en-tête
 */
export const genererEnteteHTML = (companyInfo) => {
  const info = formaterInformationsJuridiques(companyInfo);
  
  return `
    <div style="border-bottom: 2px solid #3b82f6; padding-bottom: 20px; margin-bottom: 20px;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <div style="display: flex; align-items: flex-start; gap: 20px; flex: 1;">
          ${companyInfo.logoUrl ? `
          <div style="flex-shrink: 0;">
            <img 
              src="${companyInfo.logoUrl}" 
              alt="Logo ${info.identiteComplete}" 
              style="width: 80px; height: 80px; object-fit: contain; border-radius: 8px;"
            />
          </div>
          ` : ''}
          <div style="flex: 1;">
            <h1 style="color: #1f2937; font-size: 24px; font-weight: bold; margin: 0 0 8px 0;">
              ${info.identiteComplete}
            </h1>
            <p style="color: #6b7280; margin: 0 0 4px 0; font-size: 14px;">
              ${info.formeJuridiqueComplete}
            </p>
            <p style="color: #6b7280; margin: 0 0 4px 0; font-size: 14px;">
              ${info.adresseFormatee}
            </p>
            <p style="color: #6b7280; margin: 0 0 4px 0; font-size: 14px;">
              SIREN : ${info.informationsLegales.siren} - SIRET : ${info.informationsLegales.siret}
            </p>
            ${info.informationsLegales.tvaIntracommunautaire ? `
            <p style="color: #6b7280; margin: 0; font-size: 14px;">
              TVA Intracommunautaire : ${info.informationsLegales.tvaIntracommunautaire}
            </p>
            ` : ''}
          </div>
        </div>
        <div style="text-align: right;">
          ${info.coordonnees.telephone ? `
          <p style="color: #6b7280; margin: 0 0 4px 0; font-size: 14px;">
            📞 ${info.coordonnees.telephone}
          </p>
          ` : ''}
          ${info.coordonnees.email ? `
          <p style="color: #6b7280; margin: 0 0 4px 0; font-size: 14px;">
            ✉️ ${info.coordonnees.email}
          </p>
          ` : ''}
          ${info.coordonnees.siteWeb ? `
          <p style="color: #6b7280; margin: 0; font-size: 14px;">
            🌐 ${info.coordonnees.siteWeb}
          </p>
          ` : ''}
        </div>
      </div>
    </div>
  `;
};

/**
 * Génère le pied de page HTML pour les documents
 * @param {Object} companyInfo - Informations de la société
 * @returns {string} HTML du pied de page
 */
export const genererPiedPageHTML = (companyInfo) => {
  const info = formaterInformationsJuridiques(companyInfo);
  
  return `
    <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 40px; font-size: 12px; color: #6b7280;">
      <div style="text-align: center;">
        <p style="margin: 0 0 8px 0;">
          ${info.identiteComplete} - ${info.adresseFormatee}
        </p>
        <p style="margin: 0 0 8px 0;">
          ${info.coordonnees.telephone ? `Tél : ${info.coordonnees.telephone}` : ''}
          ${info.coordonnees.email ? ` - Email : ${info.coordonnees.email}` : ''}
          ${info.coordonnees.siteWeb ? ` - Site : ${info.coordonnees.siteWeb}` : ''}
        </p>
        <p style="margin: 0;">
          SIREN : ${info.informationsLegales.siren} - SIRET : ${info.informationsLegales.siret}
          ${info.informationsLegales.tvaIntracommunautaire ? ` - TVA Intracommunautaire : ${info.informationsLegales.tvaIntracommunautaire}` : ''}
        </p>
      </div>
    </div>
  `;
};

/**
 * Valide les informations de la société
 * @param {Object} companyInfo - Informations à valider
 * @returns {Object} Résultat de la validation
 */
export const validerInformationsSociete = (companyInfo) => {
  const erreurs = [];
  const avertissements = [];
  
  // Validation SIREN
  if (companyInfo.siren) {
    const sirenNettoye = companyInfo.siren.replace(/\s/g, '');
    if (!/^\d{9}$/.test(sirenNettoye)) {
      erreurs.push('Le SIREN doit contenir exactement 9 chiffres');
    }
  }
  
  // Validation SIRET
  if (companyInfo.siret) {
    const siretNettoye = companyInfo.siret.replace(/\s/g, '');
    if (!/^\d{14}$/.test(siretNettoye)) {
      erreurs.push('Le SIRET doit contenir exactement 14 chiffres');
    }
  }
  
  // Validation email
  if (companyInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyInfo.email)) {
    erreurs.push('L\'email principal n\'est pas valide');
  }
  
  if (companyInfo.emailSecondaire && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyInfo.emailSecondaire)) {
    avertissements.push('L\'email secondaire n\'est pas valide');
  }
  
  // Validation téléphone
  if (companyInfo.telephone && !/^[\d\s\+\(\)\.-]{10,}$/.test(companyInfo.telephone)) {
    avertissements.push('Le format du téléphone semble incorrect');
  }
  
  return {
    valide: erreurs.length === 0,
    erreurs,
    avertissements
  };
};

export default {
  getCompanyInfo,
  getDefaultCompanyInfo,
  calculerTVAIntracommunautaire,
  formaterInformationsJuridiques,
  genererEnteteHTML,
  genererPiedPageHTML,
  validerInformationsSociete
};
